import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { Controller, FormProvider, useFormContext, useFormState } from 'react-hook-form';
import { useValidationResultDialog } from './dialogs';
import { Label } from './Label';
import { cn } from './utils/shadcn-utils';
const FormLabelsContext = React.createContext({});
const FormLabelsProvider = ({ children, value }) => {
    return _jsx(FormLabelsContext.Provider, { value: value, children: children });
};
FormLabelsProvider.displayName = 'FormLabelsProvider';
export const useFormLabels = () => {
    return React.useContext(FormLabelsContext);
};
const Form = ({ children, ...props }) => {
    const labels = React.useRef({});
    return (_jsx(FormLabelsProvider, { value: labels.current, children: _jsx(FormProvider, { ...props, children: children }) }));
};
export const FormValidationDialog = ({ errors, control, labels }) => {
    useValidationResultDialog(errors, { control, labels });
    return _jsx(_Fragment, {});
};
FormValidationDialog.displayName = 'FormValidationDialog';
const FormFieldContext = React.createContext({});
const FormField = ({ ...props }) => {
    return (_jsx(FormFieldContext.Provider, { value: { name: props.name }, children: _jsx(Controller, { ...props }) }));
};
const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext);
    const { getFieldState } = useFormContext();
    const formState = useFormState({ name: fieldContext.name });
    const fieldState = getFieldState(fieldContext.name, formState);
    if (!fieldContext) {
        throw new Error('useFormField should be used within <FormField>');
    }
    const { id } = itemContext;
    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState
    };
};
const FormItemContext = React.createContext({});
const FormItem = React.forwardRef(({ className, ...props }, ref) => {
    const id = React.useId();
    return (_jsx(FormItemContext.Provider, { value: { id }, children: _jsx("div", { ref: ref, className: cn('space-y-2 w-full', className), ...props }) }));
});
FormItem.displayName = 'FormItem';
const FormLabel = React.forwardRef(({ className, ...props }, ref) => {
    const { error, formItemId } = useFormField();
    return _jsx(Label, { ref: ref, className: cn('text-base', error && 'text-destructive', className), htmlFor: formItemId, ...props });
});
FormLabel.displayName = 'FormLabel';
const FormControl = React.forwardRef(({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    return _jsx(Slot, { ref: ref, id: formItemId, "aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`, "aria-invalid": !!error, ...props });
});
FormControl.displayName = 'FormControl';
const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();
    return _jsx("p", { ref: ref, id: formDescriptionId, className: cn('text-[0.8rem] text-muted-foreground', className), ...props });
});
FormDescription.displayName = 'FormDescription';
const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message ?? '') : children;
    if (!body) {
        return null;
    }
    return (_jsx("p", { ref: ref, id: formMessageId, className: cn('text-[0.8rem] font-medium text-destructive', className), ...props, children: body }));
});
FormMessage.displayName = 'FormMessage';
const array = (value) => ({ __arr: true, value });
function flattenLabels(base, spec, out) {
    if (spec == null) {
        return;
    }
    if (typeof spec === 'string') {
        out[base] = spec;
        return;
    }
    if (spec.__arr && spec.value != null) {
        const v = spec.value;
        if (typeof v === 'string') {
            out[`${base}.$`] = v;
            return;
        }
        Object.entries(v).forEach(([k, val]) => {
            const next = base ? `${base}.$.${k}` : `$.${k}`;
            flattenLabels(next, val, out);
        });
        return;
    }
    if (Array.isArray(spec) && spec.length > 0) {
        flattenLabels(base, array(spec[0]), out);
        return;
    }
    Object.entries(spec).forEach(([k, val]) => {
        const next = base ? `${base}.${k}` : k;
        flattenLabels(next, val, out);
    });
}
export function createFormLabels(_control, specOrBuilder) {
    const spec = typeof specOrBuilder === 'function' ? specOrBuilder({ array }) : specOrBuilder;
    const flat = {};
    flattenLabels('', spec, flat);
    const toWildcard = (p) => p.replace(/(?:^|\.)((?:\d+))(?=\.|$)/g, '.$');
    const get = (path) => (path ? (flat[path] ?? flat[toWildcard(path)]) : undefined);
    const t = (path) => get(path);
    return Object.assign({}, flat, { get, t, all: flat });
}
export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
//# sourceMappingURL=form.js.map